import { useManager } from 'dreamstate';
import React, { ChangeEvent, FormEvent, useCallback, useRef, useState } from 'react';

import {
  rem,
  Flex,
  Box,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Input,
  IconButton,
  Switch,
} from '@vimeo/bokeh';
import { Send } from '@vimeo/bokeh-icons';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';
import { ISelectedEmoji } from '@vimeo/live-core/components/emoji';
import { browserConfig } from '@vimeo/live-core/configs/BrowserConfig';
import { interactionToolsConfig } from '@vimeo/live-core/configs/InteractionToolsConfig';
import {
  TNotificationHandler,
  useSimpleNotification,
} from '@vimeo/live-core/hooks/live/useActionConfirmation';
import { IQnAContext, QnAManager } from '@vimeo/live-core/store/firebase/interaction/qna';
import { trackAskQuestion } from '@vimeo/live-core/tracking/tracking_engagement_qna';
import { stopEventPropagation } from '@vimeo/live-core/utils/dom/event';
import { FAILED_STATUS_COLOR } from '@vimeo/live-core/utils/styling/colors';

import { createInteractionDomName } from '../../../../libs/shared/utils/dom/identifying';
import { translations } from '../../../../translations';
import { EmojiButton } from '../../shared/EmojiButton';

interface IQnaInputFormProps extends IBaseDomElementProps {
  isDisabled?: boolean;
  qnaContext?: IQnAContext;
}

export function QnaInputForm({
  id = createInteractionDomName('qna-question-input-form'),
  className = createInteractionDomName('qna-question-input-form'),
  isDisabled = false,
  // eslint-disable-next-line react-hooks/rules-of-hooks
  qnaContext: { isAnonymousQuestionsDisabled, qnaActions } = useManager(
    QnAManager,
    ({ isAnonymousQuestionsDisabled }) => [isAnonymousQuestionsDisabled],
  ),
}: IQnaInputFormProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [question, setQuestion] = useState<string>('');
  const [anonymous, setAnonymous] = useState<boolean>(false);
  const [submitTimeout, setSubmitTimeout] = useState<boolean>(false);

  const notify: TNotificationHandler = useSimpleNotification();

  const onQuestionEdited = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setQuestion(event.target.value);
  }, []);

  const onAnonymousToggle = useCallback(() => {
    setAnonymous((it) => !it);
    inputRef.current?.focus();
  }, [setAnonymous]);

  const onSubmitQuestion = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      event.stopPropagation();

      if (!question.length || question.length > interactionToolsConfig.QNA.MAX_QUESTION_LENGTH) {
        return;
      }

      if (submitTimeout) {
        return;
      } else {
        setSubmitTimeout(true);
      }

      try {
        await qnaActions.submitQuestion(question, anonymous);

        setQuestion('');

        trackAskQuestion();
        notify({ message: translations.questionSubmitted });

        setTimeout(
          () => setSubmitTimeout(false),
          interactionToolsConfig.QNA.QUESTION_ASKING_THROTTLE,
        );
      } catch (error) {
        setSubmitTimeout(false);
      }
    },
    [question, submitTimeout, qnaActions, anonymous, notify],
  );

  const onEmojiSelect = useCallback((selection: ISelectedEmoji) => {
    if (inputRef.current) {
      const cursorPosition = inputRef.current.selectionStart || 0;
      const value = inputRef.current.value;
      const newValue =
        value.slice(0, cursorPosition) +
        selection.native +
        value.slice(cursorPosition, value.length);
      setQuestion(newValue);
      inputRef.current.focus();
    }
  }, []);

  const canInput: boolean = !isDisabled;
  const canSubmit: boolean = Boolean(
    canInput &&
      question.length &&
      question.length <= interactionToolsConfig.QNA.MAX_QUESTION_LENGTH &&
      !submitTimeout,
  );
  const charsLeft: number = interactionToolsConfig.QNA.MAX_QUESTION_LENGTH - question.length;
  const isCharsNearLimit: boolean = charsLeft <= 20;
  const isCharsOverLimit: boolean = charsLeft < 0;

  return (
    <Box
      id={id}
      className={className}
      as={'form'}
      display={'flex'}
      flexDirection={'column'}
      width={'100%'}
      paddingTop={rem(8)}
      onSubmit={onSubmitQuestion}
      onKeyUp={stopEventPropagation}
      onKeyDown={stopEventPropagation}
      onKeyPress={stopEventPropagation}
    >
      <Flex
        id={createInteractionDomName(id, 'title')}
        className={createInteractionDomName(className, 'title')}
        justifyContent={'space-between'}
        alignItems={'center'}
        marginBottom={rem(12)}
        color={'text-primary'}
        fontSize={'text-xs'}
        fontWeight={'bold'}
      >
        <div>{translations.askQuestion}</div>

        {isAnonymousQuestionsDisabled ? null : (
          <Flex alignItems={'center'} gap={rem(8)}>
            <Flex fontSize={'text-xs'} fontWeight={500}>
              {translations.anonymous}
            </Flex>

            <Switch
              id={createInteractionDomName(id, 'anonymous-toggle')}
              className={createInteractionDomName(className, 'anonymous-toggle')}
              size={'sm'}
              isChecked={anonymous}
              onChange={onAnonymousToggle}
            />
          </Flex>
        )}
      </Flex>

      <InputGroup
        id={createInteractionDomName(id, 'controls')}
        className={createInteractionDomName(className, 'controls')}
      >
        {browserConfig.BROWSER?.isMobile ? null : (
          <InputLeftElement>
            <EmojiButton
              id={createInteractionDomName(id, 'emoji-button')}
              className={createInteractionDomName(className, 'emoji-button')}
              inputRef={inputRef}
              isSubmitted={submitTimeout}
              placement={'top-start'}
              isDisabled={!canInput}
              onEmojiSelect={onEmojiSelect}
            />
          </InputLeftElement>
        )}

        <Input
          id={createInteractionDomName(id, 'input')}
          className={createInteractionDomName(className, 'input')}
          ref={inputRef}
          maxLength={interactionToolsConfig.CHAT.MAX_MESSAGE_LENGTH}
          value={question}
          autoComplete={'off'}
          isDisabled={!canInput}
          size={'md'}
          onChange={onQuestionEdited}
        />

        <InputRightElement>
          <IconButton
            id={createInteractionDomName(id, 'send-button')}
            className={createInteractionDomName(className, 'send-button')}
            aria-label={'qna-submit-button'}
            data-chat-submit
            type={'submit'}
            size={'sm'}
            variant={'minimal'}
            icon={<Send />}
            isDisabled={!canSubmit}
            transition={'none'}
            _hover={{
              backgroundColor: 'transparent!important',
            }}
          />
        </InputRightElement>
      </InputGroup>

      <Flex
        id={createInteractionDomName(id, 'info')}
        className={createInteractionDomName(className, 'info')}
        justifyContent={'space-between'}
        alignContent={'center'}
        width={'100%'}
      >
        <Flex
          id={createInteractionDomName(id, 'characters-count')}
          className={createInteractionDomName(className, 'characters-count')}
          as={'span'}
          alignItems={'center'}
          fontSize={'text-xs'}
          lineHeight={'text-xs'}
          padding={`${rem(8)} 0 0`}
          fontWeight={isCharsNearLimit || isCharsOverLimit ? 'bold' : 400}
          color={isCharsOverLimit ? FAILED_STATUS_COLOR : 'text-secondary'}
        >
          {charsLeft} {translations.characters}
        </Flex>
      </Flex>
    </Box>
  );
}
